/**
 * Settings → how this firm's advice is provided.
 *
 * The question is deliberately not "do you have an AFSL?". For an advice firm the answer is always
 * yes and it tells you nothing; what an advice document needs to know is the ROUTE the advice takes,
 * because that decides which entities it must name. Asking *how* separates the three structures in
 * one question, and each reveals only the fields that apply to it.
 *
 * Sign-up captures one of the three — the licensee and its AFSL — so a practice that is a corporate
 * authorised representative has had nowhere to record its own name and number, and an SOA from that
 * firm names one entity where a compliant attribution names three.
 */
import React, { useEffect, useState } from 'react';
import { getTenant, listFirmMembers, updateFirmMember, updateFirmLicensing, type Tenant } from '../../services/tenantApi.ts';

interface FirmLicensingSettingsProps {
    tenantId: string;
    /** Only the principal changes the firm's licensing; everyone else reads it. */
    isPrincipal: boolean;
    /** The signed-in person, whose own ASIC number is recorded against them rather than the firm. */
    myUserId: string;
}

/** Just the licensing slice of the firm record — everything this screen reads or writes. */
type Licensing = Pick<Tenant, 'afslLicenseeName' | 'afslNumber' | 'adviceStructure' | 'carName' | 'carNumber'>;

type Structure = NonNullable<Tenant['adviceStructure']>;

const OPTIONS: { value: Structure; title: string; detail: string }[] = [
    { value: 'own_afsl', title: 'Under our own AFSL', detail: 'The practice is the licensee.' },
    {
        value: 'corporate_ar',
        title: 'Through our practice, as a corporate authorised representative',
        detail: 'The practice is authorised by a licensee.',
    },
    {
        value: 'individual_ar',
        title: 'By me personally, as an authorised representative',
        detail: 'Authorised by a licensee, with no practice company.',
    },
];

const card = {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    boxShadow: '0 1px 3px rgba(15,23,42,.06)',
    marginTop: 14,
    overflow: 'hidden',
} as const;
const label = { fontSize: 10.5, fontWeight: 700, letterSpacing: '.07em', textTransform: 'uppercase', color: '#64748b', display: 'block', marginBottom: 6 } as const;
const input = { width: '100%', border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 11px', fontSize: 14, color: '#0f172a', background: '#fff' } as const;
const primaryBtn = { background: '#1a6fd4', color: '#fff', border: 0, borderRadius: 8, padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer' } as const;

export function FirmLicensingSettings({ tenantId, isPrincipal, myUserId }: FirmLicensingSettingsProps) {
    const [tenant, setTenant] = useState<Licensing | null>(null);
    const [structure, setStructure] = useState<Structure | null>(null);
    const [licensee, setLicensee] = useState('');
    const [afsl, setAfsl] = useState('');
    const [carName, setCarName] = useState('');
    const [carNumber, setCarNumber] = useState('');
    /**
     * The signer's OWN number. A different entity's from the CAR number above — that one belongs to
     * the practice company, this one to the person — and both are named on an advice document.
     * Stored against the membership, not the firm, because it follows someone between practices.
     */
    const [adviserNumber, setAdviserNumber] = useState('');
    const [state, setState] = useState<{ busy: boolean; message: string | null; ok: boolean }>({ busy: false, message: null, ok: false });

    // Read the firm here rather than threading it through the portal: this panel is the only screen
    // that wants these five fields, and the fields arrive asynchronously either way.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            const t = await getTenant(tenantId);
            if (cancelled || !t) return;
            setTenant(t);
            setStructure(t.adviceStructure ?? null);
            setLicensee(t.afslLicenseeName ?? '');
            setAfsl(t.afslNumber ?? '');
            setCarName(t.carName ?? '');
            setCarNumber(t.carNumber ?? '');
            const me = (await listFirmMembers(tenantId).catch(() => [])).find((m) => m.userId === myUserId);
            if (!cancelled) setAdviserNumber(me?.adviserNumber ?? '');
        })();
        return () => {
            cancelled = true;
        };
    }, [tenantId, myUserId]);

    const save = async () => {
        setState({ busy: true, message: null, ok: false });
        const res = await updateFirmLicensing(tenantId, {
            adviceStructure: structure,
            afslLicenseeName: licensee.trim(),
            afslNumber: afsl.trim(),
            // Cleared rather than left stale when the structure no longer has a practice company —
            // an out-of-date CAR number on an advice document is worse than none.
            carName: structure === 'corporate_ar' ? carName.trim() || null : null,
            carNumber: structure === 'corporate_ar' ? carNumber.trim() || null : null,
        });
        // The person's number is a separate write because it lives on the membership, not the firm.
        // Attempted even when the firm save failed would be misleading, so it waits on that succeeding.
        const numberRes = res.ok ? await updateFirmMember(tenantId, myUserId, { adviserNumber: adviserNumber.trim() || null }) : res;
        const ok = res.ok && numberRes.ok;
        setState({ busy: false, ok, message: ok ? 'Saved.' : (res.message ?? numberRes.message ?? 'Could not save.') });
    };

    return (
        <div style={card}>
            <div style={{ padding: '18px 20px 4px' }}>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>How your advice is provided</div>
                <p style={{ fontSize: 13, color: '#64748b', lineHeight: 1.55, margin: 0 }}>
                    This decides which entities a Statement of Advice has to name. Every SOA records what was
                    true the day it was generated, so changing this does not rewrite advice already given.
                </p>
            </div>

            <div style={{ padding: '14px 20px 20px' }}>
                {!tenant && <div style={{ fontSize: 13, color: '#64748b' }}>Loading your licensing…</div>}
                <div style={{ display: 'grid', gap: 9 }}>
                    {OPTIONS.map((opt) => (
                        <button
                            key={opt.value}
                            type="button"
                            disabled={!isPrincipal}
                            onClick={() => setStructure(opt.value)}
                            style={{
                                textAlign: 'left',
                                cursor: isPrincipal ? 'pointer' : 'default',
                                background: structure === opt.value ? '#f0f7ff' : '#fff',
                                border: structure === opt.value ? '2px solid #1a6fd4' : '1px solid #e2e8f0',
                                borderRadius: 12,
                                padding: structure === opt.value ? 12 : 13,
                            }}
                        >
                            <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a', marginBottom: 3 }}>{opt.title}</div>
                            <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.45 }}>{opt.detail}</div>
                        </button>
                    ))}
                </div>

                {structure && (
                    <div style={{ marginTop: 16, paddingLeft: 14, borderLeft: '2px solid #1a6fd4', background: '#f8fbff', borderRadius: '0 8px 8px 0', padding: '13px 14px 14px' }}>
                        {structure === 'corporate_ar' && (
                            <>
                                <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: '#1a6fd4', marginBottom: 8, fontWeight: 700 }}>
                                    The practice
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 11, marginBottom: 16 }}>
                                    <div>
                                        <label style={label} htmlFor="car-name">Practice (CAR) name</label>
                                        <input id="car-name" style={input} value={carName} disabled={!isPrincipal} onChange={(e) => setCarName(e.target.value)} />
                                    </div>
                                    <div>
                                        <label style={label} htmlFor="car-number">CAR number</label>
                                        <input id="car-number" style={input} value={carNumber} disabled={!isPrincipal} onChange={(e) => setCarNumber(e.target.value)} />
                                    </div>
                                </div>
                            </>
                        )}

                        <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: '#1a6fd4', marginBottom: 8, fontWeight: 700 }}>
                            {structure === 'own_afsl' ? 'Your licence' : 'The licensee'}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 11 }}>
                            <div>
                                <label style={label} htmlFor="licensee-name">{structure === 'own_afsl' ? 'Licensee name' : 'Licensee'}</label>
                                <input id="licensee-name" style={input} value={licensee} disabled={!isPrincipal} onChange={(e) => setLicensee(e.target.value)} />
                            </div>
                            <div>
                                <label style={label} htmlFor="afsl-number">AFSL number</label>
                                <input id="afsl-number" style={input} value={afsl} disabled={!isPrincipal} onChange={(e) => setAfsl(e.target.value)} />
                            </div>
                        </div>

                        <div style={{ fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', color: '#1a6fd4', margin: '16px 0 8px', fontWeight: 700 }}>
                            You
                        </div>
                        <div style={{ maxWidth: 240 }}>
                            <label style={label} htmlFor="my-adviser-number">Your ASIC adviser number</label>
                            <input
                                id="my-adviser-number"
                                style={input}
                                value={adviserNumber}
                                disabled={!isPrincipal}
                                onChange={(e) => setAdviserNumber(e.target.value)}
                                placeholder="e.g. 123456"
                            />
                        </div>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0', lineHeight: 1.5 }}>
                            {structure === 'own_afsl'
                                ? 'Your number on the Financial Advisers Register. As an employee or director of your own licensee you are a representative of it, not an authorised representative, and advice from this firm will not describe you as one. Colleagues’ numbers are recorded against them in Your team, below.'
                                : 'Your number on the Financial Advisers Register — the same number as your authorised representative appointment, and a different entity’s from the CAR number above. Colleagues’ numbers are recorded against them in Your team, below.'}
                        </p>
                    </div>
                )}

                {isPrincipal && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                        <button type="button" style={primaryBtn} disabled={state.busy || !structure} onClick={() => void save()}>
                            {state.busy ? 'Saving…' : 'Save licensing'}
                        </button>
                        {state.message && (
                            <span style={{ fontSize: 13, color: state.ok ? '#059669' : '#b91c1c' }}>{state.message}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
